import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, TextField, Typography, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { action } from '../../../services/api';

export default function DiagnoseModal({ open, tunnelId, tunnelName, onClose }) {
    const [target, setTarget] = useState('');
    const [count, setCount] = useState(4);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const reset = () => { setResult(null); setError(''); };

    const runPing = async () => {
        if (!target) { setError('Informe o IP de destino.'); return; }
        reset(); setLoading(true);
        try {
            const res = await action('/admin/tunnel', tunnelId, 'ping', { target, count: Number(count) });
            setResult({ kind: 'ping', data: res.data?.data || res.data });
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Falha no ping');
        } finally { setLoading(false); }
    };

    const runTraceroute = async () => {
        if (!target) { setError('Informe o IP de destino.'); return; }
        reset(); setLoading(true);
        try {
            const res = await action('/admin/tunnel', tunnelId, 'traceroute', { target });
            setResult({ kind: 'traceroute', data: res.data?.data || res.data });
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Falha no traceroute');
        } finally { setLoading(false); }
    };

    const handleClose = () => { reset(); setTarget(''); setCount(4); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Diagnóstico — {tunnelName || `tunnel ${tunnelId}`}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="IP de destino"
                        value={target}
                        onChange={e => setTarget(e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="192.168.1.1"
                        autoFocus
                    />
                    <TextField
                        label="Count"
                        type="number"
                        value={count}
                        onChange={e => setCount(e.target.value)}
                        inputProps={{ min: 1, max: 10 }}
                        size="small"
                        sx={{ width: 100 }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button variant="contained" onClick={runPing} disabled={loading}>Ping</Button>
                    <Button variant="outlined" onClick={runTraceroute} disabled={loading}>Traceroute</Button>
                    {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
                </Box>

                {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

                {result?.kind === 'ping' && <PingResult data={result.data} />}
                {result?.kind === 'traceroute' && <TracerouteResult data={result.data} />}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

function PingResult({ data }) {
    const alive = !!data?.alive;
    const rtt = data?.rtt_ms;
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                    label={alive ? 'alive' : 'down'}
                    color={alive ? 'success' : 'error'}
                    size="small"
                />
                <Typography variant="body2">
                    {data?.received}/{data?.transmitted} pacotes
                    {rtt && ` — rtt ${rtt.min}/${rtt.avg}/${rtt.max} ms`}
                </Typography>
            </Box>
            <RawOutput raw={data?.raw} />
        </Box>
    );
}

function TracerouteResult({ data }) {
    const hops = data?.hops || [];
    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
                {hops.length} hop{hops.length === 1 ? '' : 's'}
            </Typography>
            <Box component="ol" sx={{ pl: 3, mb: 1, '& li': { fontFamily: 'monospace', fontSize: 13 } }}>
                {hops.map((h, i) => (
                    <li key={i}>
                        {h.timeout
                            ? <span style={{ color: '#888' }}>* * * timeout</span>
                            : <>{h.ip} <span style={{ color: '#666' }}>({h.rtt_ms} ms)</span></>}
                    </li>
                ))}
            </Box>
            <RawOutput raw={data?.raw} />
        </Box>
    );
}

function RawOutput({ raw }) {
    if (!raw) return null;
    return (
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, minHeight: 32 }}>
                <Typography variant="caption">Saída crua</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <Box component="pre" sx={{
                    fontFamily: 'monospace', fontSize: 12, m: 0, p: 1,
                    bgcolor: '#f5f5f5', color: '#333', borderRadius: 1,
                    whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto',
                }}>{raw}</Box>
            </AccordionDetails>
        </Accordion>
    );
}
